import type {
  BaseQueryApi,
  BaseQueryFn,
  FetchArgs,
  FetchBaseQueryError,
  FetchBaseQueryMeta,
  QueryReturnValue,
} from '@reduxjs/toolkit/query/react';

import type { ErrorType } from '~/data/ErrorRecord';
import type { ErrorResponse } from '~/types/error-responses.types';
import type BaseException from '~/utils/fetch-wrapper/errors/BaseException';
import JSONParseErrorException from '~/utils/fetch-wrapper/errors/JSONParseErrorException';
import ServerMaintenanceRedirectException from '~/utils/fetch-wrapper/errors/ServerMaintenanceRedirectException';
import NoSubscriptionException from '~/utils/fetch-wrapper/errors/NoSubscriptionException';
import DIAMException from '~/utils/fetch-wrapper/errors/DIAMException';
import ErrorResponseException from '~/utils/fetch-wrapper/errors/ErrorResponseException';
import NetworkException from '~/utils/fetch-wrapper/errors/NetworkException';
import NotFoundException from '~/utils/fetch-wrapper/errors/NotFoundException';
import UnknownException from '~/utils/fetch-wrapper/errors/UnknownException';
import {
  authenticatedBaseMobileQuery,
  authenticatedBaseQuery,
  publicBaseQuery,
  publicLicensesQuery,
} from '~/api/utils/base-queries';
import {
  isDiamError,
  isUnauthenticatedError,
} from '~/utils/fetch-wrapper/middlewares/return-if-ok-or-throw';
import Store from '~/store/Store';
import { logoutUser } from '~/ducks/authentication/actions';
import logger from '~/utils/logger/logger';
import { mutex } from '~/domain/auth/utils/auth-lock';
import { replayWhileUnauthorized } from '~/domain/auth/utils/refresh-replay';
import {
  refreshToken,
  shouldUseNativeRefreshToken,
} from '~/domain/native/utils/refreshTokenNative';
import { AppState } from '~/reducers';

export type BffExtraOptions = {
  isMobileApi?: boolean;
  isPublic?: boolean;
  isLicensesQuery?: boolean;
};

const authenticatedBaseQueryWithErrorHandling: BaseQueryFn<
  string | FetchArgs,
  unknown,
  ErrorType,
  BffExtraOptions
> = async (args, api, extraOptions) => {
  await mutex.waitForUnlock();
  const isMobileApi = extraOptions?.isMobileApi;
  const result = isMobileApi
    ? await authenticatedBaseMobileQuery(args, api, extraOptions)
    : await authenticatedBaseQuery(args, api, extraOptions);
  return await handleResponseAndErrors(result, api, args, extraOptions);
};

const publicBaseQueryWithErrorHandling: BaseQueryFn<
  string | FetchArgs,
  unknown,
  ErrorType,
  BffExtraOptions
> = async (args, api, extraOptions) => {
  const result = await publicBaseQuery(args, api, extraOptions);
  return await handleResponseAndErrors(result, api, args, extraOptions);
};

export const bffBaseQuery: BaseQueryFn<
  string | FetchArgs,
  unknown,
  ErrorType,
  BffExtraOptions
> = async (args, api, extraOptions) => {
  if (extraOptions?.isPublic) {
    return await publicBaseQueryWithErrorHandling(args, api, extraOptions);
  }

  if (extraOptions?.isLicensesQuery) {
    // Skips handleResponseAndErrors: errors are raw FetchBaseQueryError. Pinned.
    return (await publicLicensesQuery(args, api, extraOptions)) as QueryReturnValue<
      unknown,
      ErrorType,
      FetchBaseQueryMeta
    >;
  }

  return await authenticatedBaseQueryWithErrorHandling(
    { ...(args as FetchArgs), credentials: 'include' },
    api,
    extraOptions
  );
};

const handleResponseAndErrors = async (
  result: QueryReturnValue<unknown, FetchBaseQueryError, FetchBaseQueryMeta>,
  api: BaseQueryApi,
  args: string | FetchArgs,
  extraOptions: BffExtraOptions
) => {
  result = await handleUnauthenticatedError(result, api, extraOptions, args);
  if (!result.error) {
    return result;
  }

  const { status, data } = result.error;
  const knownException = getKnownException(result.error, api);
  /**
   * A 401 answering the current-user probe means "no session yet", not "session expired".
   * The root reducer tears the whole store down for any action carrying `status: 401`, so
   * the probe's error has to stay status-less. Only the status is dropped: whatever reason
   * came back — a DIAM error is the only known exception a 401 can carry — is what tells
   * us why the probe failed.
   */
  const isCurrentUserProbe =
    isUnauthenticatedError(result.error) &&
    Boolean(result.meta?.request?.url?.endsWith('/users/current'));
  let err: BaseException;

  if (isCurrentUserProbe) {
    err = getStatuslessProbeError(knownException?.firstError);
  } else if (knownException) {
    err = knownException;
  } else if (isUnauthenticatedError(result.error)) {
    api.dispatch({
      type: 'UNAUTHENTICATED',
      error: {
        status: 401,
      },
    });
    err = getHttpStatusError(401, data);
  } else if (typeof status === 'number') {
    err = getHttpStatusError(status, data);
  } else if ((data as any)?.errors) {
    err = new ErrorResponseException(data as ErrorResponse<any>);
  } else {
    err = UnknownException.from();
  }
  return {
    error: err.firstError,
  };
};

const getStatuslessProbeError = (reason?: ErrorType) =>
  reason ? UnknownException.from({ ...reason, status: undefined }) : UnknownException.from();

/**
 * A 409 is a stale write; a 400 is an ordinary rejection whose details must remain
 * available to the UI. Only the first error reaches `firstError`, so the list rides
 * along on it.
 */
const getHttpStatusError = (status: number, data: unknown) => {
  /** Spring BFFs wrap every validation error in this; lambda BFFs send none at all. */
  const responseErrors = (data as { errors?: unknown } | undefined)?.errors;

  if (Array.isArray(responseErrors) && responseErrors.length > 0) {
    const errors = (responseErrors as ErrorResponse<any>['errors']).map((responseError) => ({
      ...responseError,
      status,
    }));

    return new ErrorResponseException({ errors: [{ ...errors[0], errors }] });
  }

  // Lambda BFFs answer with flat RFC 7807 problem details and no `errors` envelope.
  // `description` stays the langKey the legacy error UI renders; the problem's
  // human-readable reason travels in `detail`, where callers look for it.
  const problem = (data ?? {}) as { detail?: unknown };

  return new ErrorResponseException({
    errors: [
      {
        status,
        description: 'web.error.internal_error',
        ...(typeof problem.detail === 'string' ? { detail: problem.detail } : {}),
      },
    ],
  });
};

const getKnownException = (error: FetchBaseQueryError, api: BaseQueryApi) => {
  const { status, data } = error;

  if (status === 'FETCH_ERROR') {
    return new NetworkException();
  }
  if (status === 'PARSING_ERROR') {
    return new JSONParseErrorException();
  }
  if (status === 307) {
    api.dispatch({
      type: 'SERVER_MAINTENANCE',
      exception: new ServerMaintenanceRedirectException(),
    });
    return new ServerMaintenanceRedirectException();
  }
  if (status === 402) {
    api.dispatch({
      type: 'NO_SUBSCRIPTION',
      exception: new NoSubscriptionException(),
    });
    return new NoSubscriptionException();
  }
  // If the bff endpoint does not exist, the spring boot application will throw a 404 error
  // without "errors" in the response.
  if (status === 404 && !(data as any)?.errors) {
    return new NotFoundException();
  }
  if (isDiamError(data)) {
    return new DIAMException(data);
  }
};

const replayUnauthorizedQuery = (
  args: string | FetchArgs,
  api: BaseQueryApi,
  extraOptions: BffExtraOptions
) =>
  replayWhileUnauthorized(
    async () => await authenticatedBaseQuery(args, api, extraOptions),
    (replayed) => replayed.error?.status === 401
  );

type RefreshResult = { status: number | undefined; refreshed?: boolean };

const requestRefresh = async (
  api: BaseQueryApi,
  extraOptions: BffExtraOptions
): Promise<RefreshResult> => {
  if (shouldUseNativeRefreshToken()) {
    return { status: await refreshToken() };
  }
  const refreshRTKResult = await authenticatedBaseQuery(
    {
      method: 'POST',
      url: `${process.env.ENDPOINT_BFF}/token/refresh`,
      credentials: 'include',
    },
    api,
    extraOptions
  );
  return {
    status: refreshRTKResult.meta?.response?.status,
    refreshed: (refreshRTKResult.data as { refreshed?: boolean } | undefined)?.refreshed,
  };
};

const refreshAndReplay = async (
  result: QueryReturnValue<unknown, FetchBaseQueryError, FetchBaseQueryMeta>,
  api: BaseQueryApi,
  extraOptions: BffExtraOptions,
  args: string | FetchArgs
) => {
  logger.debug('RTK acquiring lock');
  const release = await mutex.acquire();
  try {
    const refreshResult = await requestRefresh(api, extraOptions);
    logger.debug('RTK Unauthenticated ', refreshResult.status);

    if (refreshResult.status === 200) {
      logger.log('RTK token refreshed');
      return refreshResult.refreshed === false
        ? await replayUnauthorizedQuery(args, api, extraOptions)
        : await authenticatedBaseQuery(args, api, extraOptions);
    }

    logger.log('RTK token refreshed failed', refreshResult.status);
    dispatchLogoutOnAuthFailure(api, refreshResult.status);
    return result;
  } finally {
    release();
  }
};

async function handleUnauthenticatedError(
  result: QueryReturnValue<unknown, FetchBaseQueryError, FetchBaseQueryMeta>,
  api: BaseQueryApi,
  extraOptions: BffExtraOptions,
  args: string | FetchArgs
) {
  if (result.error?.status !== 401) {
    return result;
  }
  logger.log('RTK Unauthenticated error');

  if (!mutex.isLocked()) {
    return await refreshAndReplay(result, api, extraOptions, args);
  }

  await mutex.waitForUnlock();
  return (
    checkIfUserIsAlreadyUnauthenticated(api, args) ??
    (await replayUnauthorizedQuery(args, api, extraOptions))
  );
}

/***
 * Logout on an explicit auth rejection (401) when the user is still marked as authenticated in the store *
 */
const dispatchLogoutOnAuthFailure = (api: BaseQueryApi, refreshStatus: number | undefined) => {
  if (refreshStatus !== 401) {
    return;
  }
  if ((api.getState() as AppState).authentication.authenticated) {
    Store.dispatch(logoutUser());
  }
};

/**
 * Checks if the user is already unauthenticated in the store.
 * If so, we skip follow-up requests to avoid multiple unauthenticated errors.
 * We pass through requests to /token and /token/refresh to allow token refresh attempts.
 */
export const checkIfUserIsAlreadyUnauthenticated = (
  api: BaseQueryApi,
  args: string | FetchArgs
) => {
  const currentState = api.getState() as AppState;
  const url = typeof args === 'string' ? args : args.url;
  const isAuthRequest = url?.includes('/token') || url?.includes('/users/current');
  if (!currentState.authentication.authenticated && !isAuthRequest) {
    return {
      error: {
        status: 401,
        data: { message: 'Session expired, skipping request' },
      } satisfies FetchBaseQueryError,
    };
  }
};
